/** The named page error for a Study Builder that could not open; never a blank test. */
import { shopifyComponentCopy } from '@testfeed/product-language'
import { Banner } from './Banner'
import { PageNotFound } from '../PageNotFound'
import type { BuilderLoadErrorWords } from '../../../src/shared/testfeed-connection-status-contract'
import type { PageErrorName } from '../../page-state'

export function StudyBuilderLoadError({
  name,
  words,
  setupId,
  onBack,
  onRetry,
}: {
  name: PageErrorName
  /** The Worker-owned title, body and retry verdict for this load state; the app picks no wording, only the layout. */
  words: BuilderLoadErrorWords
  /** The saved test being opened, passed to support from the full-page errors. */
  setupId?: string
  onBack: () => void
  onRetry?: () => void
}) {
  // Missing/unopenable records and transient load failures share the full-page layout; only transient failures
  // offer retry. Other named refusals retain their banner. The words themselves are always the server's.
  if (name === 'not-found')
    return <PageNotFound heading={words.title} body={words.body} setupId={setupId} onBack={onBack} />
  if (name === 'unexpected' || name === 'invalid-data')
    return <PageNotFound heading={words.title} body={words.body} setupId={setupId} errorName={name} onBack={onBack} />
  if (name === 'network') {
    const action =
      words.canRetry && onRetry
        ? { content: shopifyComponentCopy.studySetupCard.tryAgain026, onAction: onRetry }
        : undefined
    return (
      <PageNotFound
        heading={words.title}
        body={words.body}
        setupId={setupId}
        errorName={name}
        onBack={onBack}
        action={action}
      />
    )
  }
  return (
    <Banner tone="critical" heading={words.title} attributes={{ 'data-page-error': name }}>
      <s-stack gap="base">
        <s-paragraph>{words.body}</s-paragraph>
        {words.canRetry && onRetry ? (
          <s-button onClick={onRetry}>{shopifyComponentCopy.studySetupCard.tryAgain026}</s-button>
        ) : null}
      </s-stack>
    </Banner>
  )
}
